import { inject, Injectable } from '@angular/core';
import Echo from 'laravel-echo';
import { environment } from '@env/environment';
import { Transport } from 'pusher-js/types/src/core/config';
import Pusher from 'pusher-js';
import { RouteModel, UserModel } from '@app/models';
import { AuthService } from './auth.service';
import { RouteRepository, SyncRepository, UserRepository } from '@app/repositories';
import { Router } from '@angular/router';
import { AlertService } from './alert.service';

@Injectable({
    providedIn: 'root'
})
export class WebSocketService
{
    private echo: Echo<'reverb'> | null = null;

    private authService = inject(AuthService);
    private routeRepository = inject(RouteRepository);
    private userRepository = inject(UserRepository);
    private syncRepository = inject(SyncRepository);
    private router = inject(Router);
    private alertService = inject(AlertService);

    async initialize()
    {
        (window as any).Pusher = Pusher;

        if (!this.authService.isLoggedIn())
        {
            console.info('user is not logged in, prevent WS from initialization');

            return;
        }

        if (this.echo)
        {
            this.unsubscribeFromAll();
            this.echo.disconnect();
            this.echo = null;
        }

        const headers: any = {
            'Accept': 'application/json',
        };

        const token = await this.userRepository.getAuthToken();

        if (token)
        {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const reverbOptions = environment.reverb;

        try
        {
            this.echo = new Echo({
                broadcaster: reverbOptions.broadcaster as 'reverb',
                key: reverbOptions.key,
                wsHost: reverbOptions.wsHost,
                wsPort: reverbOptions.wsPort,
                wssPort: reverbOptions.wssPort,
                forceTLS: reverbOptions.forceTLS,
                enabledTransports: reverbOptions.enabledTransports as Transport[],
                disableStats: reverbOptions.disableStats,
                authEndpoint: reverbOptions.authEndpoint,
                auth: {
                    headers: headers,
                },
            });

            // Check connection status
            this.echo.connector.pusher.connection.bind('connected', () =>
            {
                console.log('✅ Connected to Reverb!');
            });

            this.echo.connector.pusher.connection.bind('error', (err: any) =>
            {
                console.error('❌ Connection error:', err);
            });

            this.echo.connector.pusher.connection.bind('disconnected', () =>
            {
                console.log('⚠️ Disconnected from Reverb');
            });
        }
        catch (e: any)
        {
            console.error('echo error', e);
        }
    }

    async listenForForceLogoutEvents(): Promise<void>
    {
        if (!this.echo)
        {
            await this.initialize();
        }

        if (this.echo)
        {
            const user = await this.userRepository.getLoggedInUser();

            if (!user)
            {
                return;
            }

            this.unsubscribeFromForceLogoutEvents(user);

            this.echo.private(`App.Models.User.${user.id}`)
                .listen('DriverForcedToLogOutEvent', async (event: any) =>
                {
                    console.info('DriverForcedToLogOutEvent received', event);

                    const activeRoute = await this.routeRepository.getActiveRoute();

                    if (activeRoute !== null)
                    {
                        console.info('forced logged out but on route so skip', event);
                        this.unsubscribeFromForceLogoutEvents(user);

                        return;
                    }

                    if (event && event.userToLogOutId && event.userToLogOutId === user.id)
                    {
                        await this.syncRepository.clearSyncedData();
                        this.authService.isLoggedIn.set(false);
                        await this.router.navigate(['login'], { replaceUrl: true, });

                        await this.alertService.show('You has been force logged out!');

                        this.unsubscribeFromAll();
                    }
                })
                .error((error: any) =>
                {
                    console.error('❌ Subscription error:', error);
                });
        }
    }

    async listenForRouteEvents(route: RouteModel): Promise<void>
    {
        if (!this.echo)
        {
            await this.initialize();
        }

        if (this.echo)
        {
            const user = await this.userRepository.getLoggedInUser();

            if (!user)
            {
                return;
            }

            this.unsubscribeFromRouteEvents(route);

            this.echo.private(`routes.${route.id}`)
                .listen('RouteCanceledEvent', async (event: any) =>
                {
                    console.info('RouteCanceledEvent received', event);

                    if (event && event.routeId && event.userId && event.routeId === route.id && event.userId === user.id)
                    {
                        await this.syncRepository.clearSyncedData();
                        this.authService.isLoggedIn.set(false);
                        await this.router.navigate(['login'], { replaceUrl: true, });

                        await this.alertService.show('Your route has been canceled!');

                        this.unsubscribeFromAll();
                    }
                })
                .error((error: any) =>
                {
                    console.error('❌ Subscription error:', error);
                });
        }
    }

    unsubscribeFromRouteEvents(route: RouteModel)
    {
        if (this.echo)
        {
            this.echo.leave(`routes.${route.id}`);
        }
    }

    unsubscribeFromForceLogoutEvents(user: UserModel)
    {
        if (this.echo)
        {
            this.echo.leave(`App.Models.User.${user.id}`);
        }
    }

    unsubscribeFromAll()
    {
        if (this.echo)
        {
            this.echo.leaveAllChannels();
            this.echo.disconnect();
            this.echo = null;
        }
    }
}
